// Copyright (c) Said Borna. All rights reserved.
import { useState, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const loginSchema = z.object({
    email: z.string().email('Enter a valid email address'),
    password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

/**
 * Login form component with email + password.
 * Enterprise design: white card, centered, authoritative.
 */
export function LoginForm(): ReactElement {
    const { signIn } = useAuth();
    const [serverError, setServerError] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema),
        defaultValues: { email: '', password: '' },
    });

    const onSubmit = async (values: LoginFormValues): Promise<void> => {
        setServerError(null);

        const { error } = await signIn(values.email, values.password);

        if (error) {
            setServerError(
                error.message === 'Invalid login credentials'
                    ? 'Invalid email or password'
                    : error.message,
            );
        }
    };

    return (
        <div className="w-full max-w-[400px] rounded-xl border border-border bg-white p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <div className="mb-6 text-center">
                <h1 className="text-[28px] font-bold tracking-tight text-foreground">BAM 2.0</h1>
                <p className="mt-1 text-sm text-muted-foreground">Brand About Me</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {serverError && (
                    <div className="rounded-lg bg-[#FEE2E2] p-3 text-sm text-[#991B1B]">
                        {serverError}
                    </div>
                )}

                <div className="space-y-1.5">
                    <label htmlFor="email" className="text-sm font-medium text-foreground">
                        Email
                    </label>
                    <input
                        id="email"
                        type="email"
                        autoComplete="email"
                        placeholder="name@company.com"
                        className={cn(
                            'flex h-10 w-full rounded-lg border border-input bg-white px-3 py-2 text-sm',
                            'placeholder:text-muted-foreground',
                            'focus-visible:outline-none focus-visible:border-foreground focus-visible:ring-2 focus-visible:ring-foreground/10 focus-visible:bg-[#F9FAFB]',
                            'transition-all duration-150',
                            errors.email && 'border-destructive',
                        )}
                        {...register('email')}
                    />
                    {errors.email && (
                        <p className="text-xs text-destructive">{errors.email.message}</p>
                    )}
                </div>

                <div className="space-y-1.5">
                    <label htmlFor="password" className="text-sm font-medium text-foreground">
                        Password
                    </label>
                    <input
                        id="password"
                        type="password"
                        autoComplete="current-password"
                        placeholder="••••••••"
                        className={cn(
                            'flex h-10 w-full rounded-lg border border-input bg-white px-3 py-2 text-sm',
                            'placeholder:text-muted-foreground',
                            'focus-visible:outline-none focus-visible:border-foreground focus-visible:ring-2 focus-visible:ring-foreground/10 focus-visible:bg-[#F9FAFB]',
                            'transition-all duration-150',
                            errors.password && 'border-destructive',
                        )}
                        {...register('password')}
                    />
                    {errors.password && (
                        <p className="text-xs text-destructive">{errors.password.message}</p>
                    )}
                </div>

                <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="h-10 w-full"
                >
                    {isSubmitting ? 'Signing in...' : 'Sign in'}
                </Button>
            </form>

            <p className="mt-6 text-center text-xs text-[#9CA3AF]">
                Access is by invitation only.
            </p>
        </div>
    );
}
